import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import { MonthKeyHelper } from '../helpers/MonthKeyHelper';
import {
  COMPARISON_BASIS_LABELS,
  PERIOD_GRAIN_LABELS,
} from '../helpers/PeriodHelper';
import type {
  ComparisonBasis,
  IPeriodFilter,
  MonthKey,
  PeriodGrain,
} from '../types/period';

const RANGE_VALUE = 'range';
/** Matches the `sm` Button height so toolbar selects line up with the actions. */
const TOOLBAR_TRIGGER_CLASS = 'h-8 py-sm rounded-sm';
const OPEN_MONTHS_HELP = 'Shown as preliminary. Not included in totals.';

interface IPeriodControlsProps {
  value: IPeriodFilter;
  onChange: (next: IPeriodFilter) => void;
  /** Every month in the dataset, ascending, including open ones (shown disabled). */
  months: MonthKey[];
  booksClosedThrough: MonthKey;
  /** Accounting toolbar layout: field labels are screen-reader only. */
  toolbar?: boolean;
  idPrefix?: string;
}

interface IFieldProps {
  id: string;
  label: string;
  toolbar: boolean;
  children: React.ReactNode;
}

const Field = ({ id, label, toolbar, children }: IFieldProps) => (
  <div className="flex flex-col gap-xs">
    <Label
      htmlFor={id}
      className={cn(
        toolbar ? 'sr-only' : 'txt-extra-small-regular text-text-tertiary'
      )}
    >
      {label}
    </Label>
    {children}
  </div>
);

export interface IMonthSelectProps {
  id: string;
  label: string;
  prefix?: string;
  toolbar: boolean;
  value: MonthKey;
  onChange: (month: MonthKey) => void;
  months: MonthKey[];
  booksClosedThrough: MonthKey;
}

export const MonthSelect = ({
  id,
  label,
  prefix,
  toolbar,
  value,
  onChange,
  months,
  booksClosedThrough,
}: IMonthSelectProps) => (
  <Field id={id} label={label} toolbar={toolbar}>
    <Select value={value} onValueChange={(v) => v && onChange(v)}>
      <SelectTrigger
        id={id}
        className={cn('w-[150px]', toolbar && TOOLBAR_TRIGGER_CLASS)}
        data-no-drag
      >
        <SelectValue>
          {prefix && toolbar ? `${prefix} ` : ''}
          {MonthKeyHelper.label(value)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {[...months].reverse().map((m) => (
          <SelectItem key={m} value={m} disabled={m > booksClosedThrough}>
            {MonthKeyHelper.label(m)}
            {m > booksClosedThrough ? ' (open)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

const isGrain = (value: string): value is PeriodGrain =>
  value in PERIOD_GRAIN_LABELS;

const isComparison = (value: string): value is ComparisonBasis =>
  value in COMPARISON_BASIS_LABELS;

export const PeriodControls = ({
  value,
  onChange,
  months,
  booksClosedThrough,
  toolbar = false,
  idPrefix = 'report',
}: IPeriodControlsProps) => {
  const isRange = Boolean(value.rangeStart);

  const handleGrainChange = (next: string) => {
    if (next === RANGE_VALUE) {
      onChange({ ...value, rangeStart: value.anchor });
    } else if (isGrain(next)) {
      onChange({ ...value, grain: next, rangeStart: null });
    }
  };

  const handleRangeStartChange = (m: MonthKey) =>
    onChange({
      ...value,
      rangeStart: m,
      anchor: m > value.anchor ? m : value.anchor,
    });

  const handleAnchorChange = (m: MonthKey) =>
    onChange({
      ...value,
      anchor: m,
      rangeStart:
        value.rangeStart && value.rangeStart > m ? m : value.rangeStart,
    });

  return (
    <div className="flex flex-wrap items-end gap-lg">
      <Field id={`${idPrefix}-grain`} label="Period" toolbar={toolbar}>
        <Select
          value={isRange ? RANGE_VALUE : value.grain}
          onValueChange={handleGrainChange}
        >
          <SelectTrigger
            id={`${idPrefix}-grain`}
            className={cn('w-[170px]', toolbar && TOOLBAR_TRIGGER_CLASS)}
            data-no-drag
          >
            <SelectValue>
              {isRange ? 'Month range' : PERIOD_GRAIN_LABELS[value.grain]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PERIOD_GRAIN_LABELS).map(([grain, label]) => (
              <SelectItem key={grain} value={grain}>
                {label}
              </SelectItem>
            ))}
            <SelectItem value={RANGE_VALUE}>Month range</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {isRange && (
        <MonthSelect
          id={`${idPrefix}-range-start`}
          label="From"
          prefix="From"
          toolbar={toolbar}
          value={value.rangeStart ?? value.anchor}
          onChange={handleRangeStartChange}
          months={months}
          booksClosedThrough={booksClosedThrough}
        />
      )}

      <MonthSelect
        id={`${idPrefix}-anchor`}
        label={isRange ? 'To' : 'As of'}
        prefix={isRange ? 'To' : undefined}
        toolbar={toolbar}
        value={value.anchor}
        onChange={handleAnchorChange}
        months={months}
        booksClosedThrough={booksClosedThrough}
      />

      <Field id={`${idPrefix}-comparison`} label="Compare to" toolbar={toolbar}>
        <Select
          value={value.comparison}
          onValueChange={(v) =>
            isComparison(v) && onChange({ ...value, comparison: v })
          }
        >
          <SelectTrigger
            id={`${idPrefix}-comparison`}
            className={cn('w-[190px]', toolbar && TOOLBAR_TRIGGER_CLASS)}
            data-no-drag
          >
            <SelectValue>
              {toolbar ? 'vs ' : ''}
              {COMPARISON_BASIS_LABELS[value.comparison]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(COMPARISON_BASIS_LABELS).map(([basis, label]) => (
              <SelectItem key={basis} value={basis}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <div className="flex flex-col gap-xs">
        <Tooltip content={OPEN_MONTHS_HELP} disabled={!toolbar}>
          <label
            htmlFor={`${idPrefix}-open-months`}
            className={cn(
              'flex cursor-pointer items-center gap-sm txt-small-regular text-text-secondary',
              toolbar && 'h-8'
            )}
            data-no-drag
          >
            <Switch
              id={`${idPrefix}-open-months`}
              checked={value.includeOpenMonths ?? false}
              onCheckedChange={(checked) =>
                onChange({ ...value, includeOpenMonths: checked })
              }
            />
            Include open months
          </label>
        </Tooltip>
        {!toolbar && (
          <p className="txt-extra-small-regular text-text-tertiary">
            {OPEN_MONTHS_HELP}
          </p>
        )}
      </div>
    </div>
  );
};
